
import { Button } from "@/components/ui/button";
import { Play, Youtube, Video } from "lucide-react";
import { useState, useEffect } from "react";
import VideoEmbed from "@/components/VideoEmbed";
import { getStorageUrl, STORAGE_BUCKETS } from "@/utils/storageUtils";

interface VideoPreviewProps {
  youtubeId?: string;
  vimeoId?: string;
  videoPlatform?: "youtube" | "vimeo";
  title: string;
  thumbnailPath?: string;
}

export const VideoPreview = ({
  youtubeId,
  vimeoId,
  videoPlatform,
  title,
  thumbnailPath,
}: VideoPreviewProps) => {
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [vimeoThumbnail, setVimeoThumbnail] = useState<string | null>(null);

  // Determine which video to show based on platform or available IDs
  const getVideoConfig = () => {
    if (videoPlatform === "vimeo" && vimeoId) {
      return { type: "vimeo", id: vimeoId, icon: Video };
    }
    if (videoPlatform === "youtube" && youtubeId) {
      return { type: "youtube", id: youtubeId, icon: Youtube };
    }
    // Fallback logic - only if no platform is specified
    if (!videoPlatform) {
      if (vimeoId) {
        return { type: "vimeo", id: vimeoId, icon: Video };
      }
      if (youtubeId) {
        return { type: "youtube", id: youtubeId, icon: Youtube };
      }
    }
    return null;
  };

  const videoConfig = getVideoConfig();

  // Fetch Vimeo thumbnail
  useEffect(() => {
    if (videoConfig?.type === "vimeo" && videoConfig.id) {
      const fetchVimeoThumbnail = async () => {
        try {
          const response = await fetch(
            `https://vimeo.com/api/v2/video/${videoConfig.id}.json`
          );
          if (response.ok) {
            const data = await response.json();
            if (data && data[0] && data[0].thumbnail_medium) {
              setVimeoThumbnail(data[0].thumbnail_medium);
            }
          }
        } catch (error) {
          console.error("Failed to fetch Vimeo thumbnail:", error);
        }
      };

      fetchVimeoThumbnail();
    }
  }, [videoConfig]);

  if (!videoConfig) {
    return (
      <div className="text-sm text-gray-500 italic">No video available</div>
    );
  }

  const getThumbnailUrl = () => {
    // Priority 1: Use uploaded thumbnail if available
    if (thumbnailPath) {
      // If it's a storage path, construct the URL; otherwise use as-is (for external URLs)
      if (!thumbnailPath.startsWith('http')) {
        return getStorageUrl(STORAGE_BUCKETS.EPISODE_THUMBNAILS, thumbnailPath);
      }
      return thumbnailPath;
    }
    
    // Priority 2: Use video source thumbnail as fallback
    if (videoConfig.type === "youtube") {
      return `https://img.youtube.com/vi/${videoConfig.id}/mqdefault.jpg`;
    }
    if (videoConfig.type === "vimeo") {
      return (
        vimeoThumbnail ||
        "https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=300&h=200&fit=crop"
      );
    }
    return "";
  };

  const IconComponent = videoConfig.icon;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-sm">
        <IconComponent className="h-4 w-4" />
        <span className="capitalize font-medium">{videoConfig.type} Video</span>
        <span className="text-gray-500">ID: {videoConfig.id}</span>
      </div>

      <div className="relative group aspect-video">
        <img
          src={getThumbnailUrl()}
          alt={`${title} preview`}
          className="w-full h-full object-cover rounded border"
        />
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center rounded">
          <Button
            onClick={() => setIsVideoOpen(true)}
            size="sm"
            className="bg-white/20 hover:bg-white/30 text-white rounded-full"
          >
            <Play className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <VideoEmbed
        isOpen={isVideoOpen}
        onClose={() => setIsVideoOpen(false)}
        youtubeId={youtubeId}
        vimeoId={vimeoId}
        videoPlatform={videoPlatform}
        title={title}
      />
    </div>
  );
};
